import { Component, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';

export interface DaySelectionDialogData {
  title: string;
  description?: string;
  excludeDays?: string[];
  preselectedDays?: string[];
  confirmLabel?: string;
}

@Component({
  selector: 'app-day-selection-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatCheckboxModule],
  template: `
    <div class="day-selection-dialog">
      <div class="header">
        <mat-icon>calendar_today</mat-icon>
        <div>
          <h2>{{ data.title }}</h2>
          <p *ngIf="data.description">{{ data.description }}</p>
        </div>
      </div>

      <div class="checkbox-wrapper">
        <label *ngFor="let day of availableDays" class="checkbox-row">
          <mat-checkbox [checked]="isSelected(day)" (change)="onToggle(day, $event)">
            {{ day }}
          </mat-checkbox>
        </label>
      </div>

      <div class="actions">
        <button mat-stroked-button type="button" (click)="dialogRef.close()">Cancel</button>
        <button mat-flat-button color="primary" type="button" (click)="confirmSelection()">
          {{ data.confirmLabel || 'Apply' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .day-selection-dialog {
      min-width: 320px;
      max-width: 420px;
    }
    .header {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-bottom: 16px;
    }
    .header mat-icon {
      color: var(--mat-sys-primary);
    }
    h2 {
      margin: 0;
      font-size: 1.15rem;
    }
    p {
      margin: 2px 0 0;
      color: var(--mat-sys-on-surface-variant);
      font-size: 0.9rem;
    }
    .checkbox-wrapper {
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-height: 240px;
      overflow-y: auto;
    }
    .checkbox-row {
      display: block;
    }
    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 20px;
    }
  `]
})
export class DaySelectionDialogComponent {
  private readonly allWeekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  private readonly selections = signal(new Set<string>());

  get availableDays(): string[] {
    const exclude = Array.isArray(this.data?.excludeDays) ? this.data.excludeDays : [];
    return this.allWeekdays.filter((day) => !exclude.includes(day));
  }

  constructor(
    public dialogRef: MatDialogRef<DaySelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DaySelectionDialogData
  ) {
    (this.data?.preselectedDays ?? []).forEach((d) => this.toggleSelection(d, true));
  }

  isSelected(day: string): boolean {
    return this.selections().has(day);
  }

  onToggle(day: string, event: MatCheckboxChange): void {
    this.toggleSelection(day, event.checked);
  }

  private toggleSelection(day: string, selected: boolean): void {
    const normalized = day as string;
    const next = new Set(this.selections());
    if (selected) {
      next.add(normalized);
    } else {
      next.delete(normalized);
    }
    this.selections.set(next);
  }

  confirmSelection(): void {
    this.dialogRef.close(Array.from(this.selections()));
  }
}
